
import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { BASE_URL } from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import Dashboardnavbar from '../../layout/Dashboardnavbar';
import usePermission from "../../config/permissions";
import {
    AddwithWhiteCircle,
    CheckGreen,
    DeleteDustbin,
    DropdownArrow,
    PDFIcon,
    UpdatePencil,
    Attachment,
    Eye,
    CloseIcon,
} from "../AllSvg";
import { useLocation } from "react-router-dom";
import { formattedDateLong } from "../Date";
import Dashboardnavbarcopy from "../../layout/Dashboardnavbar";
import { formatCurrencyIndian, handleErrorToast } from "../CustomFunctions";

// ! *************** Document Management Sub-components ***************

const AddDocument = ({ cvId, getDocuments }) => {
    const [show, setShow] = useState(false);
    const [docNames, setDocNames] = useState("");
    const [formData, setFormData] = useState({
        exp: '',
        document_file: null
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const fileInputRef = useRef("");

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Clear file error if exists
            if (errors.document_file) {
                setErrors(prev => ({ ...prev, document_file: null }));
            }

            const symbols = "!@#$%^&*()_-+=";
            const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
            const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
            const numbers = "0123456789";
            const now = new Date();
            const date = now
                .toLocaleDateString("en-GB")
                .split("/")
                .reverse()
                .join("-");
            const time = now
                .toLocaleTimeString("en-GB", { hour12: false })
                .replace(/:/g, "-");
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = "";
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${file.name}`;
            const customFile = new File([file], customFileName, { type: file.type });

            setFormData({
                ...formData,
                document_file: customFile,
            });
            setDocNames(customFileName);

            // Set input state for visual feedback
            setInputState({
                ...inputState,
                document_file: "green"
            });
        } else {
            setDocNames("");
            setInputState({
                ...inputState,
                document_file: ""
            });
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            document_file: null
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setDocNames("");
        setInputState({
            ...inputState,
            document_file: ""
        });
        // Clear file error if exists
        if (errors.document_file) {
            setErrors(prev => ({ ...prev, document_file: null }));
        }
    };

    const handleClose = () => {
        setFormData({
            exp: '',
            document_file: null
        });
        setDocNames("");
        setErrors({});
        setInputState({});
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate exp (optional but if provided, should have minimum length)
        if (!formData.exp) {
            newErrors.exp = "Experience details is required";
        }

        // Validate document_file (required)
        if (!formData.document_file) {
            newErrors.document_file = "Document file is required";
        } else {
            // Check file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(formData.document_file.type)) {
                newErrors.document_file = "Only PDF, DOC, DOCX, JPG, JPEG, PNG files are allowed";
            }

            // Check file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (formData.document_file.size > maxSize) {
                newErrors.document_file = "File size must be less than 5MB";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            const loadingToastId = toast.loading("Uploading document...");

            const formDataToSend = new FormData();
            formDataToSend.append('exp', formData.exp);
            formDataToSend.append('cv', cvId);

            if (formData.document_file) {
                formDataToSend.append('document_file', formData.document_file);
            }

            try {
                const res = await axios.post(
                    `${BASE_URL}/siteproject/documentcv/`,
                    formDataToSend
                );

                if (res.status === 200 || res.status === 201) {
                    await getDocuments();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("Document added successfully!");
                }
            } catch (err) {
                handleErrorToast(err);
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    return (
        <>
            <button
                title="Add Document"
                className="upload-svg-small"
                onClick={handleShow}
            >
                <AddwithWhiteCircle /> Add Document
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="small-modal">
                <Modal.Header closeButton className="task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Add Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="register-from">
                        <div className="form-flex-wrap">
                            <div className="flex-column width-100">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Experience Details: <span className="required">*</span>
                                </label>
                                <input
                                    name="exp"
                                    type="number"
                                    placeholder="Enter experience details"
                                    value={formData.exp}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.exp ? "error" : inputState.exp ? "success" : ""}`}
                                />
                                {errors.exp && <span className="error-message font-size-text">{errors.exp}</span>}
                            </div>

                            <div className="upload-button-field width-100">
                                <div title="Upload Document" className="flex-column">
                                    <label htmlFor="document_file" className="form-labels font-weight500 font-size-subheading">
                                        Upload Document<span className="required">*</span>
                                    </label>
                                    <div className="flex-row justify-between">
                                        <div>
                                            <label htmlFor="document_file" className="svg-icon">
                                                <div className={`svg-field form-input align-center ${errors.document_file ? "error" : inputState.document_file ? "success" : ""}`}>
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                type="file"
                                                id="document_file"
                                                name="document_file"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                                ref={fileInputRef}
                                            />
                                        </div>
                                        {docNames !== "" && (
                                            <div className="flex-row justify-between" style={{ width: "12vw" }}>
                                                <span className="file-name">{docNames.substring(0, 20)}...</span>
                                                <button type='button' title="Clear File" onClick={handleClear} className="file-clear clear-button">
                                                    Clear
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {errors.document_file && (
                                        <span className="error-message font-size-text">
                                            {errors.document_file}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>

                    <div className="button-models">
                        <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="model-button font-weight500 model-button-leave"
                            onClick={handleSubmit}
                        >
                            Add
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const UpdateDocument = ({ doc, getDocuments }) => {
    const [show, setShow] = useState(false);
    const [docNames, setDocNames] = useState("");
    const [formData, setFormData] = useState({
        exp: '',
        document_file: null
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const fileInputRef = useRef("");

    useEffect(() => {
        if (doc) {
            setFormData({
                exp: doc.exp || '',
                document_file: null
            });
            // Set initial input state for exp if it has value
            if (doc.exp) {
                setInputState({
                    exp: "green"
                });
            }
        }
    }, [doc]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Clear file error if exists
            if (errors.document_file) {
                setErrors(prev => ({ ...prev, document_file: null }));
            }

            const symbols = "!@#$%^&*()_-+=";
            const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
            const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
            const numbers = "0123456789";
            const now = new Date();
            const date = now
                .toLocaleDateString("en-GB")
                .split("/")
                .reverse()
                .join("-");
            const time = now
                .toLocaleTimeString("en-GB", { hour12: false })
                .replace(/:/g, "-");
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = "";
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${file.name}`;
            const customFile = new File([file], customFileName, { type: file.type });

            setFormData({
                ...formData,
                document_file: customFile,
            });
            setDocNames(customFileName);

            setInputState({
                ...inputState,
                document_file: "green"
            });
        }
    };

    const handleClear = () => {
        setFormData(prevState => ({
            ...prevState,
            document_file: null
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setDocNames("");
        setInputState({
            ...inputState,
            document_file: ""
        });
        // Clear file error if exists
        if (errors.document_file) {
            setErrors(prev => ({ ...prev, document_file: null }));
        }
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleShow = () => {
        setShow(true);
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate exp (optional but if provided, should have minimum length)
        if (!formData.exp) {
            newErrors.exp = "Experience details is required";
        }

        // Document file is optional in update, but if provided, validate it
        if (formData.document_file) {
            // Check file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(formData.document_file.type)) {
                newErrors.document_file = "Only PDF, DOC, DOCX, JPG, JPEG, PNG files are allowed";
            }

            // Check file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (formData.document_file.size > maxSize) {
                newErrors.document_file = "File size must be less than 5MB";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            const loadingToastId = toast.loading("Updating document...");

            const formDataToSend = new FormData();
            formDataToSend.append('exp', formData.exp);

            if (formData.document_file) {
                formDataToSend.append('document_file', formData.document_file);
            }

            try {
                const res = await axios.put(
                    `${BASE_URL}/siteproject/documentcvupdate/${doc.document_id}/`,
                    formDataToSend
                );

                if (res.status === 200) {
                    await getDocuments();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("Document updated successfully!");
                }
            } catch (err) {
                handleErrorToast(err);
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    return (
        <>
            <button title="Edit Document" className="model-edit-button-small" onClick={handleShow}>
                <UpdatePencil />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="small-modal">
                <Modal.Header closeButton className="task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Edit Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="register-from">
                        <div className="form-flex-wrap">
                            <div className="flex-column width-100">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Experience Details: <span className="required">*</span>
                                </label>
                                <input
                                    name="exp"
                                    type="number"
                                    placeholder="Enter experience details"
                                    value={formData.exp}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.exp ? "error" : inputState.exp ? "success" : ""}`}
                                />
                                {errors.exp && <span className="error-message font-size-text">{errors.exp}</span>}
                            </div>

                            <div className="upload-button-field width-100">
                                <div title="Upload Document" className="flex-column">
                                    <label htmlFor="update_document" className="form-labels font-weight500 font-size-subheading">
                                        Upload Document (Leave empty to keep current)
                                    </label>
                                    <div className="flex-row justify-between">
                                        <div>
                                            <label htmlFor="update_document" className="svg-icon">
                                                <div className={`svg-field form-input align-center ${errors.document_file ? "error" : inputState.document_file ? "success" : ""}`}>
                                                    <Attachment />
                                                </div>
                                            </label>
                                            <input
                                                type="file"
                                                id="update_document"
                                                name="document_file"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                                ref={fileInputRef}
                                            />
                                        </div>
                                        {docNames !== "" && (
                                            <div className="flex-row justify-between" style={{ width: "12vw" }}>
                                                <span className="file-name">{docNames.substring(0, 20)}...</span>
                                                <button type='button' title="Clear File" onClick={handleClear} className="file-clear clear-button">
                                                    Clear
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {errors.document_file && (
                                        <span className="error-message font-size-text">
                                            {errors.document_file}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>

                    <div className="button-models">
                        <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="model-button font-weight500 model-button-leave"
                            onClick={handleSubmit}
                        >
                            Update
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DeleteDocument = ({ docId, getDocuments }) => {
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleDelete = async () => {
        const loadingToastId = toast.loading("Deleting document...");
        try {
            const res = await axios.delete(
                `${BASE_URL}/siteproject/documentcvupdate/${docId}/`
            );

            if (res.status === 200) {
                await getDocuments();
                setShow(false);
                toast.dismiss(loadingToastId);
                toast.success("Document deleted successfully");
            }
        } catch (err) {
            handleErrorToast(err);
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete Document" className="model-delete-button-small" onClick={handleShow}>
                <DeleteDustbin />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="small-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="parent-div">
                        Are you sure you want to delete this document?
                        <div className="button-models">
                            <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                                Cancel
                            </button>
                            <button onClick={handleDelete} className="model-button font-weight500 model-button-delete">
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const ViewDocuments = ({ cvId, cvName }) => {
    const [show, setShow] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);

    const getDocuments = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/siteproject/cvdocument/${cvId}/`);
            setDocuments(res.data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setLoading(false);
        }
    };

    const handleShow = () => {
        getDocuments();
        setShow(true);
    };

    const handleClose = () => setShow(false);

    return (
        <>
            <button
                title="View Documents"
                className="view-docs-button"
                onClick={handleShow}
            >
                <Eye /> View Docs
            </button>

            <Modal
                show={show}
                onHide={handleClose}
                dialogClassName="request-leave large-modal"
            >
                <Modal.Header
                    closeButton
                    className="justify-center task-Tab-heading font-weight500 font-size-heading"
                >
                    <Modal.Title>Documents for {cvName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="table-css-white-background" style={{ padding: "20px" }}>
                        <div className="table-heading-flex" style={{ marginBottom: "20px" }}>
                            <div className="repo-heading font-weight500 font-size-heading">
                                Documents
                            </div>
                            <div className="field-cont">
                                <AddDocument cvId={cvId} getDocuments={getDocuments} />
                            </div>
                        </div>

                        {loading ? (
                            <div className="spinner-bgdatil"></div>
                        ) : (
                            <div className="table-box" style={{ maxHeight: "400px", overflowY: "auto" }}>
                                <table className="table-css">
                                    <thead className="table-heading">
                                        <tr className="custom-table-head-tr">
                                            <th className="align-left">Sr.No.</th>
                                            <th className="align-center">Experience Details</th>
                                            <th className="align-center">Document</th>
                                            <th className="align-center width-5vw">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documents.length > 0 ? (
                                            documents.map((doc, index) => (
                                                <React.Fragment key={doc.id}>
                                                    <tr className="tr-border-bottom">
                                                        <td colSpan="4"></td>
                                                    </tr>
                                                    <tr className="custom-table-head-td">
                                                        <td className="align-left">{index + 1}</td>
                                                        <td className="align-center">{doc.exp || '-'}</td>
                                                        <td className="align-center">
                                                            {doc.document_file && (
                                                                <a
                                                                    href={doc.document_file}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="document-link"
                                                                >
                                                                    <PDFIcon /> View
                                                                </a>
                                                            )}
                                                        </td>
                                                        <td className="align-center">
                                                            <div className="flex-row justify-evenly">
                                                                <UpdateDocument doc={doc} getDocuments={getDocuments} />
                                                                <DeleteDocument docId={doc.document_id
                                                                } getDocuments={getDocuments} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center">No documents found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="button-models" style={{ marginTop: "20px" }}>
                        <button
                            className="model-button font-weight500 model-button-cancel"
                            onClick={handleClose}
                        >
                            Close
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

// ! *************** CV Crud Models Start ***************

const AddCv = ({ designationid, getCv, showname }) => {
    const [show, setShow] = useState(false);
    const [designationList, setDesignationList] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        qualification: '',
        total_year_of_experience: '',
        contact_no: '',
        location: '',
        current_location: '',
        designation: '',
        in_house: '',
        email: '',
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [loading, setLoading] = useState(false);

    const inHouseOptions = [
        { value: 'inhouse', label: 'Inhouse' },
        { value: 'outsource', label: 'Outsource' },
    ];

    const handleClose = () => {
        setFormData({
            name: '',
            qualification: '',
            total_year_of_experience: '',
            contact_no: '',
            location: '',
            current_location: '',
            designation: '',
            in_house: '',
            email: '',
        });
        setErrors({});
        setInputState({});
        setShow(false);
    };

    const handleShow = () => {
        getDesignations();
        setFormData({
            name: '',
            qualification: '',
            total_year_of_experience: '',
            contact_no: '',
            location: '',
            current_location: '',
            designation: designationid,
            in_house: '',
            email: '',
        });
        setShow(true);
    };

    const getDesignations = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/designationbyid/${designationid}/`);
            setDesignationList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['name', 'email'];

        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email is invalid!";
        }

        if (formData.contact_no && !/^\d{10}$/.test(formData.contact_no)) {
            newErrors.contact_no = "Contact number must be 10 digits!";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true);
            const loadingToastId = toast.loading("Loading: Please wait...");

            try {
                let res = await axios.post(
                    `${BASE_URL}/siteproject/CV/`,
                    formData
                );

                if (res.status === 200 || res.status === 201) {
                    await getCv();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("CV added successfully!");
                }
            } catch (err) {
                handleErrorToast(err);
            } finally {
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };
    console.log(designationList)
    return (
        <>
            <button title="Add CV" className="upload-svg" onClick={handleShow}>
                <AddwithWhiteCircle /> {showname ? "Cv" : ''}
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave large-modal">
                <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Add CV</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="register-from">
                        <div className="form-flex-wrap">
                            {/* Name */}
                            <div className="flex-column">
                                <label htmlFor="name" className="form-labels font-weight500 font-size-heading">
                                    Designation:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter name"
                                    type="text"
                                    name="designation"
                                    onChange={handleInputChange}
                                    value={designationList[0]?.name}
                                    readOnly
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.name ? "error" : inputState.name ? "success" : ""}`}
                                />
                                {errors.designation && <span className="error-message font-size-text">{errors.designation}</span>}
                            </div>
                            <div className="flex-column">
                                <label htmlFor="name" className="form-labels font-weight500 font-size-heading">
                                    Name:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter name"
                                    type="text"
                                    name="name"
                                    onChange={handleInputChange}
                                    value={formData.name}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.name ? "error" : inputState.name ? "success" : ""}`}
                                />
                                {errors.name && <span className="error-message font-size-text">{errors.name}</span>}
                            </div>

                            {/* Email */}
                            <div className="flex-column">
                                <label htmlFor="email" className="form-labels font-weight500 font-size-heading">
                                    Email:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter email"
                                    type="email"
                                    name="email"
                                    onChange={handleInputChange}
                                    value={formData.email}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.email ? "error" : inputState.email ? "success" : ""}`}
                                />
                                {errors.email && <span className="error-message font-size-text">{errors.email}</span>}
                            </div>

                            {/* Qualification */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Qualification:
                                </label>
                                <input
                                    type="text"
                                    name="qualification"
                                    placeholder="Enter qualification"
                                    value={formData.qualification}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>

                            {/* Total Year of Experience */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Total Year of Experience:
                                </label>
                                <input
                                    type="number"
                                    name="total_year_of_experience"
                                    placeholder="Enter experience in years"
                                    value={formData.total_year_of_experience}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>

                            {/* Contact No */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Contact No:
                                </label>
                                <input
                                    type="text"
                                    name="contact_no"
                                    placeholder="Enter contact number"
                                    value={formData.contact_no}
                                    onChange={handleInputChange}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.contact_no ? "error" : ""}`}
                                />
                                {errors.contact_no && <span className="error-message font-size-text">{errors.contact_no}</span>}
                            </div>

                            {/* Designation */}
                            {/* <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Designation:
                                </label>
                                <select
                                    name="designation"
                                    value={formData.designation}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                >
                                    <option value="">Select Designation</option>
                                    {designationList.map(des => (
                                        <option key={des.id} value={des.id}>{des.name}</option>
                                    ))}
                                </select>
                            </div> */}

                            {/* In House */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    In House:
                                </label>
                                <select
                                    name="in_house"
                                    value={formData.in_house}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                >
                                    <option value="">Select Option</option>
                                    {inHouseOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Location */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Location:
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    placeholder="Enter location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>

                            {/* Current Location */}

                        </div>
                    </form>

                    <div className="button-models">
                        <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="model-button font-weight500 model-button-leave font-size-heading"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            Add
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const UpdateCv = ({ i, getCv }) => {
    const [show, setShow] = useState(false);
    const [designationList, setDesignationList] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        qualification: '',
        total_year_of_experience: '',
        contact_no: '',
        location: '',
        current_location: '',
        designation: '',
        in_house: '',
        email: '',
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [loading, setLoading] = useState(false);

    const inHouseOptions = [
        { value: 'inhouse', label: 'Inhouse' },
        { value: 'outsource', label: 'Outsource' },
    ];

    useEffect(() => {
        if (i) {
            setFormData({
                name: i.name || '',
                qualification: i.qualification || '',
                total_year_of_experience: i.total_year_of_experience || '',
                contact_no: i.contact_no || '',
                location: i.location || '',
                current_location: i.current_location || '',
                designation: i.designation || '',
                in_house: i.in_house || '',
                email: i.email || '',
            });
        }
    }, [i]);

    const handleClose = () => {
        setShow(false);
    };

    const handleShow = () => {
        getDesignations();
        setShow(true);
    };

    const getDesignations = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/designationbyid/${i?.designation}`);
            setDesignationList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ['name', 'email'];

        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Email is invalid!";
        }

        if (formData.contact_no && !/^\d{10}$/.test(formData.contact_no)) {
            newErrors.contact_no = "Contact number must be 10 digits!";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true);
            const loadingToastId = toast.loading("Loading: Please wait...");

            try {
                let res = await axios.put(
                    `${BASE_URL}/siteproject/CVUpdate/${i.id}/`,
                    formData
                );

                if (res.status === 200) {
                    await getCv();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("CV updated successfully!");
                }
            } catch (err) {
                handleErrorToast(err);
            } finally {
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    return (
        <>
            <button title="Update CV" className="model-edit-button" onClick={handleShow}>
                <UpdatePencil />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave large-modal">
                <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Update CV</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="register-from">
                        <div className="form-flex-wrap">
                            {/* Name */}
                            <div className="flex-column">
                                <label htmlFor="name" className="form-labels font-weight500 font-size-heading">
                                    Designation:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter name"
                                    type="text"
                                    name="designation"
                                    onChange={handleInputChange}
                                    value={designationList[0]?.name}
                                    readOnly
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.designation ? "error" : inputState.designation ? "success" : ""}`}
                                />
                                {errors.designation && <span className="error-message font-size-text">{errors.designation}</span>}
                            </div>
                            <div className="flex-column">
                                <label htmlFor="name" className="form-labels font-weight500 font-size-heading">
                                    Name:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter name"
                                    type="text"
                                    name="name"
                                    onChange={handleInputChange}
                                    value={formData.name}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.name ? "error" : inputState.name ? "success" : ""}`}
                                />
                                {errors.name && <span className="error-message font-size-text">{errors.name}</span>}
                            </div>

                            {/* Email */}
                            <div className="flex-column">
                                <label htmlFor="email" className="form-labels font-weight500 font-size-heading">
                                    Email:<span className="required">*</span>
                                </label>
                                <input
                                    placeholder="Enter email"
                                    type="email"
                                    name="email"
                                    onChange={handleInputChange}
                                    value={formData.email}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.email ? "error" : inputState.email ? "success" : ""}`}
                                />
                                {errors.email && <span className="error-message font-size-text">{errors.email}</span>}
                            </div>

                            {/* Qualification */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Qualification:
                                </label>
                                <input
                                    type="text"
                                    name="qualification"
                                    placeholder="Enter qualification"
                                    value={formData.qualification}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>

                            {/* Total Year of Experience */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Total Year of Experience:
                                </label>
                                <input
                                    type="number"
                                    name="total_year_of_experience"
                                    placeholder="Enter experience in years"
                                    value={formData.total_year_of_experience}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>

                            {/* Contact No */}
                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Contact No:
                                </label>
                                <input
                                    type="text"
                                    name="contact_no"
                                    placeholder="Enter contact number"
                                    value={formData.contact_no}
                                    onChange={handleInputChange}
                                    className={`form-input font-weight400 font-size-subheading form-input-background ${errors.contact_no ? "error" : ""}`}
                                />
                                {errors.contact_no && <span className="error-message font-size-text">{errors.contact_no}</span>}
                            </div>


                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    In House:
                                </label>
                                <select
                                    name="in_house"
                                    value={formData.in_house}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                >
                                    <option value="">Select Option</option>
                                    {inHouseOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>


                            <div className="flex-column">
                                <label className="form-labels font-weight500 font-size-subheading">
                                    Location:
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    placeholder="Enter location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    className="form-input font-weight400 font-size-subheading form-input-background"
                                />
                            </div>



                        </div>
                    </form>

                    <div className="button-models">
                        <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="model-button font-weight500 model-button-leave font-size-heading"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            Update
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

const DeleteCv = ({ i, getCv }) => {
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait...");

        try {
            let res = await axios.delete(
                `${BASE_URL}/siteproject/CVUpdate/${i.id}/`
            );

            if (res.status === 200) {
                await getCv();
                setShow(false);
                toast.dismiss(loadingToastId);
                toast.success("CV deleted successfully");
            }
        } catch (err) {
            handleErrorToast(err);
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete CV" className="model-delete-button" onClick={handleShow}>
                <DeleteDustbin />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="small-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete CV of "{i.name}"</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="parent-div">
                        Are you sure you want to delete this CV? This will also delete all associated documents.
                        <div className="button-models">
                            <button className="model-button font-weight500 model-button-cancel" onClick={handleClose}>
                                Cancel
                            </button>
                            <button onClick={handleFormSubmitDelete} className="model-button font-weight500 model-button-delete">
                                Delete
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};



const Cv = () => {
    const [buffer, setBuffering] = useState(true);
    const [CvData, setCvData] = useState([]);
    const location = useLocation();
    const [Designationid, setDesignationId] = useState([]);
    console.log(location.state, 'location')
    const getCv = async () => {
        setBuffering(true);
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/cvdesignation/${location.state.project}/`);
            setCvData(response.data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => {
        getCv();
        setDesignationId(location.state.project)
    }, [location]);

    return (
        <>
            {/* <Dashboardnavbar name='CV' url="Cv" /> */}
            <div className='content-tabs'>
                <div className="table-css-white-background" style={{ padding: "34px 30px" }}>
                    <div className="table-heading-flex">
                        <div className="repo-heading font-weight500 font-size-heading">
                            CV Management
                        </div>
                        <div className="field-cont">
                            <AddCv designationid={Designationid} getCv={getCv} showname={true} />
                        </div>
                    </div>

                    <div className="table-box height-73vh">
                        <table className="table-css">
                            <thead className="table-heading">
                                <tr className="custom-table-head-tr">
                                    <th className="align-left">Sr.No.</th>
                                    <th className="align-center">Name</th>
                                    <th className="align-center">Email</th>
                                    <th className="align-center">Qualification</th>
                                    <th className="align-center">Experience</th>
                                    <th className="align-center">Contact No</th>
                                    <th className="align-center">Designation</th>
                                    <th className="align-center">In House</th>
                                    <th className="align-center">Location</th>

                                    <th className="align-center">Documents</th>
                                    <th className="align-center width-5vw">Action</th>
                                </tr>
                            </thead>

                            {buffer ? (
                                <div className="spinner-bgdatil"></div>
                            ) : (
                                <tbody>
                                    {CvData.length > 0 ? CvData.map((i, index) => (
                                        <React.Fragment key={i.id}>
                                            <tr className="tr-border-bottom">
                                                <td colSpan="12"></td>
                                            </tr>
                                            <tr className="custom-table-head-td">
                                                <td className="align-left">{index + 1}</td>
                                                <td className="align-center">{i.name || '-'}</td>
                                                <td className="align-center">{i.email || '-'}</td>
                                                <td className="align-center">{i.qualification || '-'}</td>
                                                <td className="align-center">{i.total_year_of_experience || '-'}</td>
                                                <td className="align-center">{i.contact_no || '-'}</td>
                                                <td className="align-center">{i.designation_name || '-'}</td>
                                                <td className="align-center">{i.in_house || '-'}</td>
                                                <td className="align-center">{i.location || '-'}</td>

                                                <td className="align-center">
                                                    <ViewDocuments cvId={i.id} cvName={i.name} />
                                                </td>
                                                <td className="align-center">
                                                    <div className="flex-row justify-evenly">
                                                        <UpdateCv i={i} getCv={getCv} />
                                                        <DeleteCv i={i} getCv={getCv} />
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    )) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No CVs found</td>
                                        </tr>
                                    )}
                                </tbody>
                            )}
                        </table>
                    </div>

                    <ToastContainer
                        position="top-center"
                        autoClose={1000}
                        hideProgressBar={false}
                        newestOnTop={true}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                    />
                </div>
            </div>
        </>
    );
};

export { Cv, AddCv };
